import { RefObject, useEffect } from 'react';

/** Represents the axis along which scrolling can be synchronized. */
type ScrollAxis = 'vertical' | 'horizontal';

/** Configuration options for the useSyncedScroll hook. */
interface UseSyncedScrollOptions {
  /** Reference to the element whose scroll position will be synchronized to the target */
  sourceRef: RefObject<HTMLElement | null>;
  /** Reference to the element that will be scrolled to match the source */
  targetRef: RefObject<HTMLElement | null>;
  /** The axis along which to synchronize scroll (default: 'vertical') */
  axis?: ScrollAxis;
  /** Whether scroll synchronization is active (default: true) */
  enabled?: boolean;
  /** External trigger to force re-synchronization when changed */
  syncTrigger?: unknown;
}

/**
 * Gets the current scroll position of an element along a specific axis.
 * @param element - The HTML element to check
 * @param axis - The axis to measure ('vertical' or 'horizontal')
 * @returns The scroll position in pixels
 */
const getScrollPosition = (element: HTMLElement, axis: ScrollAxis) =>
  axis === 'vertical' ? element.scrollTop : element.scrollLeft;

/**
 * Sets the scroll position of an element along a specific axis.
 * @param element - The HTML element to scroll
 * @param axis - The axis to scroll on ('vertical' or 'horizontal')
 * @param value - The scroll position to set in pixels
 */
const setScrollPosition = (
  element: HTMLElement,
  axis: ScrollAxis,
  value: number
) => {
  if (axis === 'vertical') {
    element.scrollTop = value;
  } else {
    element.scrollLeft = value;
  }
};

/**
 * Calculates the maximum scroll distance available on an element along a specific axis.
 * @param element - The HTML element to measure
 * @param axis - The axis to measure ('vertical' or 'horizontal')
 * @returns The maximum scrollable distance in pixels
 */
const getMaxScroll = (element: HTMLElement, axis: ScrollAxis) =>
  axis === 'vertical'
    ? element.scrollHeight - element.clientHeight
    : element.scrollWidth - element.clientWidth;

/**
 * Synchronizes the scroll position of a target element to match the source element
 * using proportional scroll ratios, accounting for different scrollable areas.
 * @param source - The source element whose scroll position will be read
 * @param target - The target element whose scroll position will be updated
 * @param axis - The axis along which to synchronize ('vertical' or 'horizontal')
 */
const syncScrollPair = (
  source: HTMLElement,
  target: HTMLElement,
  axis: ScrollAxis
) => {
  const sourceMax = getMaxScroll(source, axis);
  const targetMax = getMaxScroll(target, axis);

  if (sourceMax <= 0 || targetMax <= 0) {
    setScrollPosition(target, axis, 0);
    return;
  }

  const ratio = getScrollPosition(source, axis) / sourceMax;
  setScrollPosition(target, axis, ratio * targetMax);
};

/**
 * Schedules a callback to run on the next animation frame, with fallback to setTimeout.
 * Returns a cleanup function to cancel the scheduled callback.
 * @param callback - The function to execute
 * @returns A cleanup function that cancels the scheduled callback
 */
const scheduleFrame = (callback: () => void) => {
  if (
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function'
  ) {
    const frameId = window.requestAnimationFrame(callback);
    return () => {
      if (typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frameId);
      }
    };
  }

  const timeoutId = globalThis.setTimeout(callback, 16);
  return () => globalThis.clearTimeout(timeoutId);
};

/**
 * Hook that synchronizes the scroll position of a target element to match a source element.
 * Maintains proportional scroll ratios when elements have different scrollable dimensions.
 * Prevents infinite scroll loops by tracking which element triggered the sync.
 * @param options - Configuration options for the hook
 * @returns void
 */
export const useSyncedScroll = ({
  sourceRef,
  targetRef,
  axis = 'vertical',
  enabled = true,
  syncTrigger,
}: UseSyncedScrollOptions) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const sourceElement = sourceRef.current;
    const targetElement = targetRef.current;

    if (!sourceElement || !targetElement) {
      return;
    }

    let isSyncingFromSource = false;
    let isSyncingFromTarget = false;

    let cancelSourceReset: (() => void) | null = null;
    let cancelTargetReset: (() => void) | null = null;

    const handleSourceScroll = () => {
      if (isSyncingFromTarget) return;

      isSyncingFromSource = true;
      syncScrollPair(sourceElement, targetElement, axis);
      cancelSourceReset?.();
      cancelSourceReset = scheduleFrame(() => {
        isSyncingFromSource = false;
        cancelSourceReset = null;
      });
    };

    const handleTargetScroll = () => {
      if (isSyncingFromSource) return;

      isSyncingFromTarget = true;
      syncScrollPair(targetElement, sourceElement, axis);
      cancelTargetReset?.();
      cancelTargetReset = scheduleFrame(() => {
        isSyncingFromTarget = false;
        cancelTargetReset = null;
      });
    };

    sourceElement.addEventListener('scroll', handleSourceScroll);
    targetElement.addEventListener('scroll', handleTargetScroll);

    syncScrollPair(sourceElement, targetElement, axis);

    return () => {
      sourceElement.removeEventListener('scroll', handleSourceScroll);
      targetElement.removeEventListener('scroll', handleTargetScroll);
      cancelSourceReset?.();
      cancelTargetReset?.();
    };
  }, [axis, enabled, sourceRef, targetRef]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const sourceElement = sourceRef.current;
    const targetElement = targetRef.current;

    if (!sourceElement || !targetElement) {
      return;
    }

    syncScrollPair(sourceElement, targetElement, axis);
  }, [axis, enabled, sourceRef, targetRef, syncTrigger]);
};
